import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  TextField,
} from "@mui/material";
import {
  Power,
  PowerOff,
  Refresh,
  RestartAlt,
  CheckCircle,
} from "@mui/icons-material";

const PHASE_CONFIG = {
  initiated: {
    label: "Power cycle command sent...",
    sublabel: "Waiting for device to respond",
    progress: 15,
  },
  power_off: {
    label: "Device is powering off",
    sublabel: "Waiting for device to go offline...",
    progress: 40,
  },
  device_offline: {
    label: "Device went offline",
    sublabel: "Waiting for device to reconnect...",
    progress: 65,
  },
  complete: {
    label: "Power cycle complete!",
    sublabel: null,
    progress: 100,
  },
};

const PowerStatusCard = ({
  device,
  onRefresh,
  isRefreshing,
  onReboot,
  rebootPhase,
}) => {
  const powerState = device?.power_state;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [duration, setDuration] = useState(10);

  const isOn = powerState === "ON";
  const isOff = powerState === "OFF";
  const isOnline = device?.status === "ONLINE";
  const isCycling = rebootPhase && rebootPhase !== "complete";

  const stateColor = isOn ? "#22c55e" : isOff ? "#ef4444" : "#9ca3af";
  const bgColor = isOn ? "#22c55e0a" : isOff ? "#ef44440a" : "#9ca3af0a";
  const borderColor = isOn ? "#22c55e30" : isOff ? "#ef444430" : "#9ca3af30";
  const stateLabel = isOn ? "Power On" : isOff ? "Power Off" : "Unknown";
  const StateIcon = isOff ? PowerOff : Power;

  const phaseInfo = rebootPhase ? PHASE_CONFIG[rebootPhase] : null;

  return (
    <Card
      sx={{
        height: "100%",
        bgcolor: isCycling ? "#f59e0b0a" : bgColor,
        border: `1px solid ${isCycling ? "#f59e0b30" : borderColor}`,
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <IconButton
        onClick={onRefresh}
        disabled={!isOnline || isRefreshing || isCycling}
        size="small"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          "@keyframes spin": {
            from: { transform: "rotate(0deg)" },
            to: { transform: "rotate(360deg)" },
          },
          ...(isRefreshing && { animation: "spin 1s linear infinite" }),
        }}
      >
        <Refresh fontSize="small" />
      </IconButton>
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
          "&:last-child": { pb: 3 },
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: "50%",
            bgcolor: isCycling ? "#f59e0b18" : `${stateColor}18`,
            border: `3px solid ${isCycling ? "#f59e0b" : stateColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
            ...(isCycling && { animation: "pulse 1.5s ease-in-out infinite" }),
          }}
        >
          {rebootPhase === "complete" ? (
            <CheckCircle sx={{ fontSize: 52, color: "#22c55e" }} />
          ) : isCycling ? (
            <RestartAlt sx={{ fontSize: 52, color: "#f59e0b" }} />
          ) : (
            <StateIcon sx={{ fontSize: 52, color: stateColor }} />
          )}
        </Box>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ lineHeight: 1, mb: 0.5 }}
        >
          Port Status
        </Typography>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            color: isCycling ? "#f59e0b" : stateColor,
            lineHeight: 1.2,
          }}
        >
          {isCycling ? "Cycling..." : stateLabel}
        </Typography>

        {/* Reboot progress feedback */}
        {phaseInfo && (
          <Box sx={{ width: "100%", mt: 2, px: 1 }}>
            <LinearProgress
              variant="determinate"
              value={phaseInfo.progress}
              color={rebootPhase === "complete" ? "success" : "warning"}
              sx={{ height: 6, borderRadius: 3, mb: 1 }}
            />
            <Typography
              variant="body2"
              fontWeight="medium"
              textAlign="center"
              sx={{
                color: rebootPhase === "complete" ? "#22c55e" : "text.primary",
              }}
            >
              {phaseInfo.label}
            </Typography>
            {phaseInfo.sublabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                display="block"
              >
                {phaseInfo.sublabel}
              </Typography>
            )}
          </Box>
        )}

        {!device?.enable_power_off && isOn && !rebootPhase && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Power off is disabled for this device
          </Typography>
        )}
        <Button
          variant="contained"
          color="warning"
          size="small"
          startIcon={<RestartAlt />}
          onClick={() => setConfirmOpen(true)}
          disabled={!isOnline || isCycling}
          sx={{ mt: 2 }}
        >
          Power Cycle
        </Button>
      </CardContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Power Cycle Device</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will cycle power off and back on. The device will be briefly
            offline.
          </DialogContentText>
          <TextField
            label="Off duration (seconds)"
            type="number"
            size="small"
            fullWidth
            value={duration}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setDuration(Math.max(1, Math.min(300, v)));
            }}
            inputProps={{ min: 1, max: 300 }}
            helperText="1–300 seconds (default: 10)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmOpen(false);
              onReboot(duration);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PowerStatusCard;
